import { atomFamily } from 'recoil';
import { isNil } from 'ramda';
import { sessionStorageEffect } from '@/utils/recoil/storageEffects';
import { DEFAULT_PAGINATION_CONFIG } from './constants';

/**
 * Canonical keys of the tables whose rows-per-page selection survives a page reload.
 * A table without a key here is not persisted.
 * For EstablishmentsMeters/EstablishmentsSupplyPoints, only pageSize lives here — page and sorts
 * live in the establishments Recoil stores (see routes/establishments/types.ts).
 */
export enum PaginationKey {
  CarbonFootprintEmissionFactors = 'carbonFootprint:emissionFactors',
  CarbonFootprintEmissionRecords = 'carbonFootprint:emissionRecords',
  Customers = 'customers',
  EstablishmentsBuildingFiles = 'establishments:buildingFiles',
  EstablishmentsBuildingNotifications = 'establishments:buildingNotifications',
  EstablishmentsBuildings = 'establishments:buildings',
  EstablishmentsList = 'establishments:list',
  EstablishmentsMeters = 'establishments:meters',
  EstablishmentsSupplyPoints = 'establishments:supplyPoints',
  ExportsList = 'exports:list',
  ExportsMeters = 'exports:meters',
  ExportsTender = 'exports:tender',
  MeterConsumption = 'meters:consumption',
  MeterNotifications = 'meters:notifications',
  Notifications = 'notifications',
  RecordsInvoice = 'records:invoice',
  RecordsManual = 'records:manual',
  RecordsOverview = 'records:overview',
  RecordsSensorConsumption = 'records:sensorConsumption',
  RecordsSensorValue = 'records:sensorValue',
  SupplyPointFiles = 'supplyPoints:files',
  SupplyPointNotifications = 'supplyPoints:notifications',
  TenantLeases = 'tenants:leases',
  TenantsBillings = 'tenants:billings',
  TenantsLeaseUnits = 'tenants:leaseUnits',
  TenantsList = 'tenants:list',
  UserActivity = 'users:activity',
  UsersAuditLogs = 'users:auditLogs',
  UsersList = 'users:list',
}

/** A `PaginationKey`, optionally suffixed to tell apart tables rendered per tab. */
export type PaginationStorageKey = PaginationKey | `${PaginationKey}:${string}`;

/**
 * Family parameter for tables that opt out of persistence. Its atom holds no effects,
 * so it neither reads nor writes session storage.
 */
export const UNPERSISTED_PAGINATION_KEY = 'unpersisted';

const PAGE_SIZE_STORAGE_PREFIX = 'enmon:pageSize:';

// Rows per page, one atom (and one session storage entry) per table
export const pageSizeStore = atomFamily<number | undefined, PaginationStorageKey | typeof UNPERSISTED_PAGINATION_KEY>({
  key: 'enmon:pageSizeStore',
  default: undefined,
  effects: storageKey =>
    storageKey === UNPERSISTED_PAGINATION_KEY ? [] : [sessionStorageEffect(`${PAGE_SIZE_STORAGE_PREFIX}${storageKey}`)],
});

/** Guards against a remembered page size that is no longer one of the offered options. */
export const isOfferedPageSize = (pageSize: number | undefined): pageSize is number =>
  !isNil(pageSize) && DEFAULT_PAGINATION_CONFIG.pageSizeOptions.includes(pageSize);

/** A stored page size, falling back to the default when nothing valid is stored. */
export const getPageSizeOrDefault = (pageSize: number | undefined): number =>
  isOfferedPageSize(pageSize) ? pageSize : DEFAULT_PAGINATION_CONFIG.defaultPageSize;
